import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import { WrapText } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { DataGrid, type Column } from "react-data-grid";
import "react-data-grid/lib/styles.css";
import { getFileRecordName } from "../contentType";
import { LoadingOverlay } from "../LoadingOverlay";
import { useFileRecordVersionPreview } from "../versioning/FileRecordVersioningContext";

type Sheet = {
  name: string;
  rows: string[][];
};

const ROW_HEIGHT = 30;
const MAX_INITIAL_COLUMN_WIDTH = 360;
/** Rows sampled when estimating initial column widths. */
const WIDTH_SAMPLE_ROWS = 50;

const WRAP_LINE_HEIGHT = 20;
const WRAP_MAX_LINES = 12;
/** Average glyph width at the grid's 13px font, used for line estimates. */
const WRAP_AVG_CHAR_PX = 6.8;

/**
 * react-data-grid themes through CSS custom properties; map them onto the
 * design-system tokens so the grid follows the app palette (and dark mode)
 * instead of shipping its own.
 */
const GRID_THEME = {
  blockSize: "100%",
  "--rdg-font-size": "13px",
  "--rdg-color": "hsl(var(--foreground))",
  "--rdg-background-color": "hsl(var(--background))",
  "--rdg-border-color": "hsl(var(--border))",
  "--rdg-header-background-color": "hsl(var(--muted))",
  "--rdg-row-hover-background-color": "hsl(var(--muted) / 0.5)",
  "--rdg-selection-color": "hsl(var(--ring))",
} as React.CSSProperties;

/**
 * Injected as a raw <style> tag rather than Tailwind utilities or a CSS
 * file: these rules must be unlayered (react-data-grid's cell rules live
 * in a CSS `@layer` that beats Tailwind's utilities layer) and must not
 * depend on Tailwind's source scanner picking up exotic arbitrary
 * variants.
 *
 * Scrollbars: the grid is the pane's only horizontal scroller, and macOS
 * overlay scrollbars stay hidden until a scroll gesture — which a mouse
 * can't even produce horizontally. Styling `::-webkit-scrollbar` opts
 * Chromium/Safari out of overlay mode so both bars are always visible.
 * Chrome 121+ ignores all `::-webkit-scrollbar` styling (and reverts to
 * overlay bars) when `scrollbar-color`/`scrollbar-width` computes to a
 * non-`auto` value — and notion-like-editor.scss sets both via a global
 * `*` rule that's bundled with the detail pane. The explicit `auto`
 * reset below undoes that leak so the webkit rules actually apply.
 *
 * Border: react-data-grid draws a 1px border around its root, boxing the
 * grid in and visually detaching it from the pane footer below; the pane
 * chrome (header border / footer border-t) already provides the dividers,
 * so drop it and let the grid bleed to the pane edges.
 *
 * Wrap mode: multi-line, top-aligned body cells; the header row stays
 * single-line.
 */
const GRID_CSS = `
.spreadsheet-grid {
  border: none;
  scrollbar-width: auto;
  scrollbar-color: auto;
}
.spreadsheet-grid::-webkit-scrollbar {
  width: 12px;
  height: 12px;
  background-color: transparent;
}
.spreadsheet-grid::-webkit-scrollbar-track,
.spreadsheet-grid::-webkit-scrollbar-corner {
  background-color: transparent;
}
.spreadsheet-grid::-webkit-scrollbar-thumb {
  border-radius: 9999px;
  background-color: hsl(var(--muted-foreground) / 0.35);
  border: 3px solid transparent;
  background-clip: padding-box;
}
.spreadsheet-grid::-webkit-scrollbar-thumb:hover {
  background-color: hsl(var(--muted-foreground) / 0.55);
}
.spreadsheet-grid--wrap .rdg-row .rdg-cell {
  white-space: normal;
  overflow-wrap: anywhere;
  align-content: start;
  padding-block: 5px;
  line-height: 20px;
}
`;

/**
 * Detect binary workbook containers by magic bytes: `PK` zip header (xlsx)
 * or the OLE/CFB header (legacy xls). We sniff bytes instead of trusting
 * `content_type` because upload paths routinely mislabel spreadsheets —
 * Windows browsers report `.csv` files as `application/vnd.ms-excel`.
 */
function sniffWorkbookContainer(bytes: Uint8Array): boolean {
  if (bytes.length < 4) return false;
  if (bytes[0] === 0x50 && bytes[1] === 0x4b) return true;
  return (
    bytes[0] === 0xd0 &&
    bytes[1] === 0xcf &&
    bytes[2] === 0x11 &&
    bytes[3] === 0xe0
  );
}

/** xlsx / xls via SheetJS — lazy-loaded so it stays out of the main bundle. */
async function parseWorkbook(buffer: ArrayBuffer): Promise<Sheet[]> {
  const XLSX = await import("xlsx");
  const workbook = XLSX.read(buffer, { type: "array" });
  return workbook.SheetNames.map((name) => {
    const rows = XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[name], {
      header: 1,
      // Formatted text (`w`) instead of raw values so dates/currencies
      // read the way the authoring app displayed them.
      raw: false,
      defval: "",
      blankrows: false,
    });
    return {
      name,
      rows: rows.map((row) => row.map((cell) => String(cell ?? ""))),
    };
  });
}

/** csv / tsv via papaparse (delimiter auto-detected). */
async function parseDelimitedText(buffer: ArrayBuffer): Promise<Sheet[]> {
  const Papa = (await import("papaparse")).default;
  // Strip a UTF-8 BOM so it doesn't end up glued to the first header cell.
  const text = new TextDecoder().decode(buffer).replace(/^\uFEFF/, "");
  const result = Papa.parse<string[]>(text, { skipEmptyLines: "greedy" });
  return [{ name: "Sheet 1", rows: result.data }];
}

function looksNumeric(cell: string): boolean {
  const trimmed = cell.trim();
  if (!trimmed || !/\d/.test(trimmed)) return false;
  return /^[$€£]?[-+]?[\d.,\s]+%?$/.test(trimmed);
}

/**
 * CSV/xlsx carry no "has header" flag, so we guess: the first row is a
 * header when it reads like labels — every cell non-empty, none numeric,
 * no duplicates. Errs toward the common labeled-export shape while letting
 * raw numeric dumps render as data from row 1.
 */
function detectHeaderRow(rows: string[][]): boolean {
  if (rows.length < 2) return false;
  const first = rows[0];
  if (first.length === 0) return false;
  const seen = new Set<string>();
  for (const cell of first) {
    const trimmed = cell.trim();
    if (!trimmed || looksNumeric(trimmed)) return false;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
  }
  return true;
}

/** 0 → "A", 25 → "Z", 26 → "AA" — Excel-style column letters. */
function columnLabel(index: number): string {
  let label = "";
  for (let i = index; i >= 0; i = Math.floor(i / 26) - 1) {
    label = String.fromCharCode(65 + (i % 26)) + label;
  }
  return label;
}

function estimateColumnWidth(rows: string[][], column: number): number {
  let width = 80;
  for (const row of rows.slice(0, WIDTH_SAMPLE_ROWS)) {
    // ~8px per character at 13px font, plus cell padding.
    const estimate = (row[column]?.length ?? 0) * 8 + 26;
    width = Math.max(width, Math.min(estimate, MAX_INITIAL_COLUMN_WIDTH));
  }
  return width;
}

/**
 * Estimated pixel height of a wrapped row: longest cell's line count
 * (capped) at the current column widths. An estimate is enough — cells
 * clip anything past it, and exact measurement would defeat virtualization.
 */
function estimateWrappedRowHeight(row: string[], widths: number[]): number {
  let lines = 1;
  for (let c = 0; c < row.length; c++) {
    const value = row[c];
    if (!value) continue;
    const charsPerLine = Math.max(
      4,
      Math.floor(((widths[c] ?? 80) - 16) / WRAP_AVG_CHAR_PX),
    );
    let cellLines = 0;
    for (const segment of value.split("\n")) {
      cellLines += Math.max(1, Math.ceil(segment.length / charsPerLine));
    }
    lines = Math.max(lines, Math.min(WRAP_MAX_LINES, cellLines));
  }
  return lines * WRAP_LINE_HEIGHT + 10;
}

/**
 * Native table preview for csv / tsv / xls / xlsx — read-only, rendered
 * with react-data-grid: virtualized rows/columns (large sheets scroll in
 * both axes), sticky header, drag-resizable columns, optional text wrap.
 *
 * The file is fetched and parsed entirely in the browser — unlike the
 * gview-based `OfficeContent`, the content never transits Google's
 * infrastructure and works on URLs only this session can access.
 */
export function SpreadsheetContent({ record }: { record: FileRecord }) {
  const { previewFetchUrl } = useFileRecordVersionPreview();
  const [sheets, setSheets] = useState<Sheet[] | null>(null);
  const [activeSheet, setActiveSheet] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wrapText, setWrapText] = useState(false);
  /** User-dragged widths by column key, layered over the estimates. */
  const [resizedWidths, setResizedWidths] = useState<ReadonlyMap<
    string,
    number
  > | null>(null);

  const fetchUrl = previewFetchUrl ?? record.url;

  useEffect(() => {
    if (!fetchUrl) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setSheets(null);
    setActiveSheet(0);
    setResizedWidths(null);

    void fetch(fetchUrl)
      .then(async (res) => {
        if (!res.ok) throw new Error(`Failed (${res.status})`);
        const buffer = await res.arrayBuffer();
        if (cancelled) return;
        const isWorkbook = sniffWorkbookContainer(
          new Uint8Array(buffer, 0, Math.min(4, buffer.byteLength)),
        );
        const parsed = isWorkbook
          ? await parseWorkbook(buffer)
          : await parseDelimitedText(buffer);
        if (!cancelled) setSheets(parsed);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fetchUrl]);

  const sheet = sheets?.[activeSheet];

  const { columns, dataRows, estimatedWidths } = useMemo(() => {
    if (!sheet || sheet.rows.length === 0) {
      return {
        columns: [] as Column<string[]>[],
        dataRows: [] as string[][],
        estimatedWidths: [] as number[],
      };
    }

    const hasHeader = detectHeaderRow(sheet.rows);
    const headerRow = hasHeader ? sheet.rows[0] : null;
    const body = hasHeader ? sheet.rows.slice(1) : sheet.rows;
    const columnCount = Math.max(
      headerRow?.length ?? 0,
      ...body.map((row) => row.length),
    );
    // Sheet row number of the first data row (1-based; header occupies row 1).
    const firstRowNumber = hasHeader ? 2 : 1;

    const widths = Array.from({ length: columnCount }, (_, c) =>
      estimateColumnWidth(sheet.rows, c),
    );

    const rowNumberColumn: Column<string[]> = {
      key: "__row-number__",
      name: "",
      width: 18 + String(body.length + firstRowNumber - 1).length * 8,
      frozen: true,
      resizable: false,
      cellClass:
        "bg-muted/50 text-right text-xs tabular-nums text-muted-foreground select-none",
      renderCell: ({ rowIdx }) => rowIdx + firstRowNumber,
    };

    const dataColumns = Array.from(
      { length: columnCount },
      (_, c): Column<string[]> => ({
        key: String(c),
        name: headerRow?.[c] ?? columnLabel(c),
        width: widths[c],
        headerCellClass: hasHeader
          ? "font-medium"
          : "text-center text-xs font-normal text-muted-foreground",
        renderCell: ({ row }) => row[c] ?? "",
      }),
    );

    return {
      columns: [rowNumberColumn, ...dataColumns],
      dataRows: body,
      estimatedWidths: widths,
    };
  }, [sheet]);

  const rowHeight = useMemo(() => {
    if (!wrapText) return ROW_HEIGHT;
    const widths = estimatedWidths.map(
      (width, c) => resizedWidths?.get(String(c)) ?? width,
    );
    return (row: string[]) => estimateWrappedRowHeight(row, widths);
  }, [wrapText, estimatedWidths, resizedWidths]);

  if (!fetchUrl) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        File not available.
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col">
      <style>{GRID_CSS}</style>
      <div className="relative min-h-0 flex-1">
        {sheet && columns.length > 1 && (
          <DataGrid
            aria-label={getFileRecordName(record)}
            className={cn(
              "spreadsheet-grid",
              wrapText && "spreadsheet-grid--wrap",
            )}
            columns={columns}
            rows={dataRows}
            rowHeight={rowHeight}
            headerRowHeight={32}
            defaultColumnOptions={{ resizable: true, minWidth: 48 }}
            onColumnResize={(column, width) => {
              setResizedWidths((prev) =>
                new Map(prev ?? []).set(column.key, width),
              );
            }}
            style={GRID_THEME}
          />
        )}
        {sheet && columns.length <= 1 && (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            This sheet is empty.
          </div>
        )}
        {isLoading && <LoadingOverlay />}
        {error && !isLoading && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
            {error}
          </div>
        )}
      </div>

      {sheet && (
        <div className="flex shrink-0 items-center gap-2 border-t bg-background px-2 py-1">
          {sheets && sheets.length > 1 && (
            <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
              {sheets.map((s, i) => (
                <button
                  key={`${s.name}-${i}`}
                  type="button"
                  onClick={() => {
                    setActiveSheet(i);
                    setResizedWidths(null);
                  }}
                  className={cn(
                    "whitespace-nowrap rounded px-2.5 py-1 text-xs",
                    i === activeSheet
                      ? "bg-accent font-medium text-accent-foreground"
                      : "text-muted-foreground hover:bg-accent/50",
                  )}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <span className="text-xs tabular-nums text-muted-foreground">
              {dataRows.length.toLocaleString()} rows × {columns.length - 1}{" "}
              cols
            </span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-pressed={wrapText}
                    onClick={() => setWrapText((v) => !v)}
                    className={cn(
                      "h-6 w-6 rounded",
                      wrapText && "bg-accent text-accent-foreground",
                    )}
                  >
                    <WrapText className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {wrapText ? "Don't wrap text" : "Wrap text"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      )}
    </div>
  );
}
